import React from 'react';
import { useDispatch } from 'react-redux';
import { Button, IconButton, ListItem, ListItemText, Tooltip } from '@mui/material';

import { Icon, ResponsiveDialog, Text, useSettings, useUserData, Utils } from 'app/main/components';
import { ViewList } from 'app/main/components/page-layouts';
import { type WbsRootNode } from './useWbsState';

interface WbsModelsListItemProps {
	model: any;
	loadModel: (newModel: any) => void;
}

interface WbsModelsLoadDialogProps {
	open: boolean;
	onClose: VoidFunction;
	root: WbsRootNode;
	updateRefresh: VoidFunction;
}

function WbsModelsListItem({ model, loadModel }: WbsModelsListItemProps) {
	const { t } = useSettings();

	return (
		<ListItem
			secondaryAction={
				<IconButton onClick={() => loadModel(model)} edge="end">
					<Icon icon="download" />
				</IconButton>
			}
		>
			<ListItemText
				slotProps={{
					primary: { component: 'div', className: 'inline-flex items-center' },
					secondary: { className: model.wbsm_description ? 'font-400' : 'italic' }
				}}
				primary={
					<>
						{model.wbsm_warning_desc && (
							<Tooltip title={model.wbsm_warning_desc} arrow>
								<Icon className="mr-2" icon="alert-circle-outline" color="warning.main" />
							</Tooltip>
						)}
						{model.wbsm_blocked_desc && (
							<Tooltip title={model.wbsm_blocked_desc} arrow>
								<Icon className="mr-2" icon="block" color="error.main" />
							</Tooltip>
						)}
						<Text className="font-600" variant="subtitle2">
							{model.wbsm_title}
						</Text>
					</>
				}
				secondary={model.wbsm_description || t('VIEW_ITEM_NO_DESCRIPTION')}
			/>
		</ListItem>
	);
}

export default function WbsModelsLoadDialog({ open, onClose, root, updateRefresh }: WbsModelsLoadDialogProps) {
	const dispatch = useDispatch();
	const { t } = useSettings();
	const userData = useUserData();

	function loadModel(model) {
		Utils.executeStored({
			action: 'st_wbs_nodes_copy',
			dispatch,
			data: {
				wbsn_table: root.table,
				wbsn_table_uid: root.tableId,
				wbsn_parent_table: 'wbs_models',
				wbsn_parent_table_uid: model.wbsm_uid,
				wbsn_user_created_uid: userData.user_uid
			},
			successHandler: response => {
				onClose();
				updateRefresh();
			}
		});
	}

	return (
		<ResponsiveDialog
			open={open}
			onClose={onClose}
			maxWidth="sm"
			fullWidth
			title={t('WBS_DIALOG_ACTION_LOAD_MODEL')}
			content={
				<ViewList
					module="wbs_models"
					itemLayout={props => <WbsModelsListItem {...props} loadModel={loadModel} />}
					// defaultFilters={widget.defaultFilters}
					showFilters={false}
					showExportButton={false}
				/>
			}
			actions={<Button onClick={onClose}>{t('DIALOG_ACTION_CLOSE')}</Button>}
			disablePadding
			dividers
		/>
	);
}
